 import type { TFunction } from 'i18next'
import type { Step, Tour } from 'nextstepjs'

/**
 * The guide is one tour per section, not a single 30-step run.
 *
 * The single run had to hop across eleven routes, so one selector that mounted
 * late stalled everything after it, and it asked a first-time user for ten
 * minutes of clicking before they could touch anything. The same 30 steps are
 * kept — they are delivered where they are relevant instead: the welcome tour
 * on first login, every other tour on the first visit to its own section.
 */
export const WELCOME_TOUR = 'welcome'
export const MENU_TOUR = 'menu'
export const REPORTS_TOUR = 'reports'
export const EMPLOYEES_TOUR = 'employees'
export const SCHEDULE_TOUR = 'schedule'
export const MOBILE_APP_TOUR = 'mobile-app'
export const TABLE_CARDS_TOUR = 'table-cards'
export const REGISTER_DEVICE_TOUR = 'register-device'
export const FEEDBACK_TOUR = 'feedback'
export const FLOOR_PLAN_TOUR = 'floor-plan'
export const LOYALTY_TOUR = 'loyalty'

/** Where the welcome tour lives, and the fallback for pages without a tour. */
export const TOUR_HOME_ROUTE = '/dashboard'

type TourRoute = {
  /** Route prefix whose pages this tour belongs to. */
  prefix: string
  tour: string
  /**
   * Page the tour may auto-start on, when it is not any page under `prefix`.
   * Needed where a tour's later steps point at anchors that only one page in
   * the section renders — starting it anywhere else would spotlight nothing.
   */
  entryRoute?: string
}

const TOUR_ROUTES: TourRoute[] = [
  { prefix: '/dashboard', tour: WELCOME_TOUR },
  {
    prefix: '/menu-management',
    tour: MENU_TOUR,
    entryRoute: '/menu-management/dishes'
  },
  {
    prefix: '/sales-reports',
    tour: REPORTS_TOUR,
    entryRoute: '/sales-reports/menu-items'
  },
  { prefix: '/employees-management', tour: EMPLOYEES_TOUR },
  { prefix: '/employees-schedule', tour: SCHEDULE_TOUR },
  { prefix: '/mobile-app-management', tour: MOBILE_APP_TOUR },
  { prefix: '/table-cards-management', tour: TABLE_CARDS_TOUR },
  { prefix: '/register-device', tour: REGISTER_DEVICE_TOUR },
  { prefix: '/feedback', tour: FEEDBACK_TOUR },
  { prefix: '/loyalty', tour: LOYALTY_TOUR },
  { prefix: '/floor-plan', tour: FLOOR_PLAN_TOUR }
]

export const ALL_TOURS = TOUR_ROUTES.map(({ tour }) => tour)

function routeForPath(pathname: string) {
  return TOUR_ROUTES.find(
    ({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

/** The tour this page belongs to — what the sidebar's replay button runs. */
export function tourForPath(pathname: string): string | null {
  return routeForPath(pathname)?.tour ?? null
}

/**
 * The tour allowed to start by itself here. Stricter than `tourForPath`: a
 * tour with an `entryRoute` only opens on that page (or on the bare section
 * route, which redirects to it), never on a sibling that lacks its anchors.
 */
export function autoStartTourForPath(pathname: string): string | null {
  const match = routeForPath(pathname)
  if (!match) return null
  if (!match.entryRoute) return match.tour
  return pathname === match.prefix || pathname === match.entryRoute
    ? match.tour
    : null
}

/**
 * Shared step chrome. A page tour opens while its page is still mounting, so a
 * selector can miss on the first lookup — the library's own retry handles that
 * far better than a guessed delay does. `showControls` / `showSkip` are absent
 * on purpose: they only drive the library's default card, and OnboardingCard
 * renders its own controls.
 */
function step(s: Step): Step {
  return {
    pointerPadding: 8,
    pointerRadius: 12,
    selectorRetryAttempts: 5,
    selectorRetryDelay: 200,
    ...s
  }
}

export function buildTours(t: TFunction): Tour[] {
  return [
    {
      // Everything reachable from the dashboard: the page itself plus the app
      // chrome that surrounds every other page. No route changes, so nothing
      // here can stall on a page that has not loaded yet.
      tour: WELCOME_TOUR,
      steps: [
        step({
          icon: 'las la-hand-paper',
          title: t('ONBOARDING.STEPS.WELCOME.TITLE'),
          content: t('ONBOARDING.STEPS.WELCOME.CONTENT'),
          side: 'bottom',
          pointerPadding: 0,
          pointerRadius: 0
        }),
        step({
          icon: 'las la-bars',
          title: t('ONBOARDING.STEPS.SIDEBAR.TITLE'),
          content: t('ONBOARDING.STEPS.SIDEBAR.CONTENT'),
          selector: '#tour-sidebar',
          side: 'right',
          pointerPadding: 0,
          pointerRadius: 0
        }),
        step({
          icon: 'las la-lock',
          title: t('ONBOARDING.STEPS.SIDEBAR_LOCK.TITLE'),
          content: t('ONBOARDING.STEPS.SIDEBAR_LOCK.CONTENT'),
          selector: '#tour-sidebar-lock',
          side: 'right'
        }),
        step({
          icon: 'las la-language',
          title: t('ONBOARDING.STEPS.LANGUAGE.TITLE'),
          content: t('ONBOARDING.STEPS.LANGUAGE.CONTENT'),
          selector: '#tour-language-switch',
          side: 'bottom-right'
        }),
        step({
          icon: 'las la-question-circle',
          title: t('ONBOARDING.STEPS.HELP.TITLE'),
          content: t('ONBOARDING.STEPS.HELP.CONTENT'),
          selector: '#tour-help-button',
          side: 'top-left'
        }),
        step({
          icon: 'las la-chart-pie',
          title: t('ONBOARDING.STEPS.DASHBOARD_OVERVIEW.TITLE'),
          content: t('ONBOARDING.STEPS.DASHBOARD_OVERVIEW.CONTENT'),
          selector: '#tour-dashboard-content',
          side: 'bottom',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-chart-area',
          title: t('ONBOARDING.STEPS.DASHBOARD_CHART.TITLE'),
          content: t('ONBOARDING.STEPS.DASHBOARD_CHART.CONTENT'),
          selector: '#tour-dashboard-chart',
          side: 'bottom',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-download',
          title: t('ONBOARDING.STEPS.DASHBOARD_EXPORT.TITLE'),
          content: t('ONBOARDING.STEPS.DASHBOARD_EXPORT.CONTENT'),
          selector: '#tour-dashboard-export',
          side: 'bottom-right'
        }),
        step({
          icon: 'las la-robot',
          title: t('ONBOARDING.STEPS.CHATBOT.TITLE'),
          content: t('ONBOARDING.STEPS.CHATBOT.CONTENT'),
          selector: '#tour-chatbot',
          side: 'top-right',
          pointerRadius: 999
        }),
        step({
          icon: 'las la-redo',
          title: t('ONBOARDING.STEPS.RESTART.TITLE'),
          content: t('ONBOARDING.STEPS.RESTART.CONTENT'),
          selector: '#tour-restart-button',
          side: 'top-left'
        }),
        step({
          icon: 'las la-award',
          title: t('ONBOARDING.STEPS.FINISH.TITLE'),
          content: t('ONBOARDING.STEPS.FINISH.CONTENT'),
          pointerPadding: 0,
          pointerRadius: 0
        })
      ]
    },
    {
      // The only tour that still walks between routes: the menu sections are
      // four sibling pages and each one needs its own explanation.
      tour: MENU_TOUR,
      steps: [
        step({
          icon: 'las la-clipboard-list',
          title: t('ONBOARDING.STEPS.MENU_OVERVIEW.TITLE'),
          content: t('ONBOARDING.STEPS.MENU_OVERVIEW.CONTENT'),
          selector: '#tour-menu-content',
          side: 'bottom-left',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-scroll',
          title: t('ONBOARDING.STEPS.MENU_TABS.TITLE'),
          content: t('ONBOARDING.STEPS.MENU_TABS.CONTENT'),
          selector: '#tour-menu-sub-nav',
          side: 'bottom-right',
          nextRoute: '/menu-management/toppings'
        }),
        step({
          icon: 'las la-pepper-hot',
          title: t('ONBOARDING.STEPS.TOPPINGS_PAGE.TITLE'),
          content: t('ONBOARDING.STEPS.TOPPINGS_PAGE.CONTENT'),
          selector: '#tour-menu-content',
          side: 'bottom-left',
          pointerRadius: 16,
          prevRoute: '/menu-management/dishes',
          nextRoute: '/menu-management/ingredients'
        }),
        step({
          icon: 'las la-carrot',
          title: t('ONBOARDING.STEPS.INGREDIENTS_PAGE.TITLE'),
          content: t('ONBOARDING.STEPS.INGREDIENTS_PAGE.CONTENT'),
          selector: '#tour-menu-content',
          side: 'bottom-left',
          pointerRadius: 16,
          prevRoute: '/menu-management/toppings',
          nextRoute: '/menu-management/data-extraction'
        }),
        step({
          icon: 'las la-camera',
          title: t('ONBOARDING.STEPS.DATA_EXTRACTION_PAGE.TITLE'),
          content: t('ONBOARDING.STEPS.DATA_EXTRACTION_PAGE.CONTENT'),
          selector: '#tour-data-extraction',
          pointerRadius: 16,
          prevRoute: '/menu-management/ingredients'
        })
      ]
    },
    {
      tour: REPORTS_TOUR,
      steps: [
        step({
          icon: 'las la-file-invoice-dollar',
          title: t('ONBOARDING.STEPS.REPORTS_TABS.TITLE'),
          content: t('ONBOARDING.STEPS.REPORTS_TABS.CONTENT'),
          selector: '#tour-reports-tabs',
          side: 'bottom-left'
        }),
        step({
          icon: 'las la-filter',
          title: t('ONBOARDING.STEPS.REPORTS_FILTERS.TITLE'),
          content: t('ONBOARDING.STEPS.REPORTS_FILTERS.CONTENT'),
          selector: '#tour-reports-filters',
          side: 'bottom-left'
        }),
        step({
          icon: 'las la-download',
          title: t('ONBOARDING.STEPS.REPORTS_EXPORT.TITLE'),
          content: t('ONBOARDING.STEPS.REPORTS_EXPORT.CONTENT'),
          selector: '#tour-reports-export',
          side: 'bottom-right',
          nextRoute: '/sales-reports/inventory'
        }),
        step({
          icon: 'las la-warehouse',
          title: t('ONBOARDING.STEPS.INVENTORY_TAB.TITLE'),
          content: t('ONBOARDING.STEPS.INVENTORY_TAB.CONTENT'),
          selector: '#tour-reports-tabs',
          side: 'bottom-left',
          prevRoute: '/sales-reports/menu-items'
        })
      ]
    },
    {
      tour: EMPLOYEES_TOUR,
      steps: [
        step({
          icon: 'las la-users-cog',
          title: t('ONBOARDING.STEPS.EMPLOYEES_TABLE.TITLE'),
          content: t('ONBOARDING.STEPS.EMPLOYEES_TABLE.CONTENT'),
          selector: '#tour-employees-table',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-medal',
          title: t('ONBOARDING.STEPS.EMPLOYEES_RANKING.TITLE'),
          content: t('ONBOARDING.STEPS.EMPLOYEES_RANKING.CONTENT'),
          selector: '#tour-ranking-button',
          side: 'bottom-right',
          pointerPadding: 6,
          pointerRadius: 999
        })
      ]
    },
    {
      tour: SCHEDULE_TOUR,
      steps: [
        step({
          icon: 'las la-calendar-alt',
          title: t('ONBOARDING.STEPS.SCHEDULE_VIEWS.TITLE'),
          content: t('ONBOARDING.STEPS.SCHEDULE_VIEWS.CONTENT'),
          selector: '#tour-schedule-views',
          side: 'bottom',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-clock',
          title: t('ONBOARDING.STEPS.SCHEDULE_TOOLBAR.TITLE'),
          content: t('ONBOARDING.STEPS.SCHEDULE_TOOLBAR.CONTENT'),
          selector: '#tour-schedule-toolbar',
          side: 'bottom',
          pointerRadius: 16
        })
      ]
    },
    {
      tour: MOBILE_APP_TOUR,
      steps: [
        step({
          icon: 'las la-mobile',
          title: t('ONBOARDING.STEPS.MOBILE_APP.TITLE'),
          content: t('ONBOARDING.STEPS.MOBILE_APP.CONTENT'),
          selector: '#tour-mobile-preview',
          side: 'right',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-paint-brush',
          title: t('ONBOARDING.STEPS.MOBILE_FORM.TITLE'),
          content: t('ONBOARDING.STEPS.MOBILE_FORM.CONTENT'),
          selector: '#tour-mobile-form',
          side: 'left',
          pointerRadius: 16
        })
      ]
    },
    {
      tour: TABLE_CARDS_TOUR,
      steps: [
        step({
          icon: 'las la-th-list',
          title: t('ONBOARDING.STEPS.TABLES_LIST.TITLE'),
          content: t('ONBOARDING.STEPS.TABLES_LIST.CONTENT'),
          selector: '#tour-tables-list',
          side: 'right',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-qrcode',
          title: t('ONBOARDING.STEPS.TABLE_CARDS.TITLE'),
          content: t('ONBOARDING.STEPS.TABLE_CARDS.CONTENT'),
          selector: '#tour-cards-list',
          side: 'left',
          pointerRadius: 16
        })
      ]
    },
    {
      tour: REGISTER_DEVICE_TOUR,
      steps: [
        step({
          icon: 'las la-tablet',
          title: t('ONBOARDING.STEPS.REGISTER_DEVICE.TITLE'),
          content: t('ONBOARDING.STEPS.REGISTER_DEVICE.CONTENT'),
          selector: '#tour-register-device',
          side: 'bottom',
          pointerRadius: 16
        })
      ]
    },
    {
      /*
        Left column first, because that is the order the page is used in: read
        what the programme promises, set the numbers behind it, save, and only
        then look at what it produced on the right.
      */
      tour: LOYALTY_TOUR,
      steps: [
        step({
          icon: 'las la-gift',
          title: t('ONBOARDING.STEPS.LOYALTY_RULES.TITLE'),
          content: t('ONBOARDING.STEPS.LOYALTY_RULES.CONTENT'),
          selector: '#tour-loyalty-rules',
          side: 'right',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-sliders-h',
          title: t('ONBOARDING.STEPS.LOYALTY_OPTIONS.TITLE'),
          content: t('ONBOARDING.STEPS.LOYALTY_OPTIONS.CONTENT'),
          selector: '#tour-loyalty-options',
          side: 'right',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-save',
          title: t('ONBOARDING.STEPS.LOYALTY_SAVE.TITLE'),
          content: t('ONBOARDING.STEPS.LOYALTY_SAVE.CONTENT'),
          selector: '#tour-loyalty-save',
          side: 'right',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-chart-area',
          title: t('ONBOARDING.STEPS.LOYALTY_STATS.TITLE'),
          content: t('ONBOARDING.STEPS.LOYALTY_STATS.CONTENT'),
          selector: '#tour-loyalty-stats',
          side: 'bottom',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-list',
          title: t('ONBOARDING.STEPS.LOYALTY_REWARDS.TITLE'),
          content: t('ONBOARDING.STEPS.LOYALTY_REWARDS.CONTENT'),
          selector: '#tour-loyalty-rewards',
          side: 'top',
          pointerRadius: 16
        })
      ]
    },
    {
      tour: FEEDBACK_TOUR,
      steps: [
        step({
          icon: 'las la-comment',
          title: t('ONBOARDING.STEPS.FEEDBACK.TITLE'),
          content: t('ONBOARDING.STEPS.FEEDBACK.CONTENT'),
          selector: '#tour-feedback-content',
          side: 'bottom-left',
          pointerRadius: 16
        })
      ]
    },
    {
      // Every anchor here is rendered unconditionally. The panel's "New table"
      // and "Selection" sections swap depending on what is selected, so neither
      // is spotlighted directly — the tools step describes the swap instead.
      tour: FLOOR_PLAN_TOUR,
      steps: [
        step({
          icon: 'las la-vector-square',
          title: t('ONBOARDING.STEPS.FLOOR_PLAN_BOARD.TITLE'),
          content: t('ONBOARDING.STEPS.FLOOR_PLAN_BOARD.CONTENT'),
          selector: '#tour-floor-plan-canvas',
          side: 'right',
          pointerRadius: 16
        }),
        step({
          icon: 'las la-door-open',
          title: t('ONBOARDING.STEPS.FLOOR_PLAN_ROOMS.TITLE'),
          content: t('ONBOARDING.STEPS.FLOOR_PLAN_ROOMS.CONTENT'),
          selector: '#tour-floor-plan-rooms',
          side: 'bottom-left'
        }),
        step({
          icon: 'las la-search-plus',
          title: t('ONBOARDING.STEPS.FLOOR_PLAN_ZOOM.TITLE'),
          content: t('ONBOARDING.STEPS.FLOOR_PLAN_ZOOM.CONTENT'),
          selector: '#tour-floor-plan-zoom',
          side: 'bottom-right'
        }),
        step({
          icon: 'las la-sliders-h',
          title: t('ONBOARDING.STEPS.FLOOR_PLAN_PANEL.TITLE'),
          content: t('ONBOARDING.STEPS.FLOOR_PLAN_PANEL.CONTENT'),
          selector: '#tour-floor-plan-panel-toggle',
          side: 'bottom-right'
        }),
        step({
          icon: 'las la-mouse-pointer',
          title: t('ONBOARDING.STEPS.FLOOR_PLAN_TOOLS.TITLE'),
          content: t('ONBOARDING.STEPS.FLOOR_PLAN_TOOLS.CONTENT'),
          selector: '#tour-floor-plan-tools-group',
          side: 'left'
        }),
        step({
          icon: 'las la-save',
          title: t('ONBOARDING.STEPS.FLOOR_PLAN_SAVE.TITLE'),
          content: t('ONBOARDING.STEPS.FLOOR_PLAN_SAVE.CONTENT'),
          selector: '#tour-floor-plan-plan-group',
          side: 'left'
        })
      ]
    }
  ]
}
